import React, { useState, useEffect } from 'react';
import { FlexContainer } from '@/components/containers/FlexContainer/FlexContainer';
import { useSelector } from 'react-redux';
import HelmetComponent from '@/components/general/Helmet/HelmetComponent';
import Label from '@/components/form/Label/Label';
import './PrivacyPolicy.scss';

const PrivacyPolicy = (props) => {
  const lenguageData = useSelector((state) => state.lenguage.lang);
  const esDescription =
    'Al reservar en Popnest, aceptas nuestras normas de uso, pago y cancelación. Respetamos tu privacidad y te pedimos cuidar el espacio y a otros usuarios. Consulta nuestros términos para más detalles.';
  const enDescription =
    'By booking with Popnest, you agree to our usage, payment, and cancellation policies. We respect your privacy and ask you to care for the space and other users. Check our terms for more details.';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <FlexContainer className={'termsView flex-column margtb40 text-justify'}>
      <HelmetComponent
        title={
          lenguageData === 'EN'
            ? 'Privacy Policy - Popnest'
            : 'Política de Privacidad - Popnest'
        }
        description={lenguageData === 'EN' ? enDescription : esDescription}
        graphTitle={
          lenguageData === 'EN'
            ? 'Terms and Conditions - Popnest'
            : 'Terminos y Condiciones - Popnest'
        }
        graphDescription={lenguageData === 'EN' ? enDescription : esDescription}
      />
      <Label className={'text-12 m-text-10'}>
        {lenguageData === 'EN' && (
          <>
            <div class="header">
              <h1>Privacy Policy</h1>
              <p>Popnest Platform Web Users</p>
              <p style={{ marginTop: '10px', fontSize: '0.95rem' }}>
                Last updated: December 22, 2025
              </p>
            </div>

            <div class="container">
              <div class="section">
                <h2>1. IDENTIFICATION OF THE DATA CONTROLLER</h2>
                <p>
                  FINOVIX, S.A.P.I. de C.V., operating under the trade name Popnest (hereinafter,
                  "Popnest", "we", "our" or "the Controller"), with address at Londres 105, Colonia
                  Del Carmen, Coyoacán, 04100 Mexico City, CDMX, Mexico, is responsible for the
                  processing of personal data we collect from users who access our website
                  popnest.org and use our online reservation platform.
                </p>
                <p>
                  This Privacy Policy is governed by the Federal Law on Protection of Personal Data
                  Held by Private Parties (LFPDPPP) and other applicable regulations in the United
                  Mexican States.
                </p>

                <h3>1.1 Scope of this Privacy Policy</h3>
                <p>
                  This Privacy Policy applies exclusively to users who make reservations, purchases,
                  or use services through the popnest.org web platform.
                </p>

                <div class="highlight-box">
                  <p>
                    <strong>IMPORTANT:</strong> FINOVIX, S.A.P.I. de C.V. also operates under the
                    trade name "Estudio Popnest" as a physical coworking space. If you make a
                    reservation directly with Estudio Popnest (either at their physical facilities
                    or through their own reservation channels), the Privacy Policy and Terms and
                    Conditions specific to Estudio Popnest will apply, which may differ from this
                    Policy.
                  </p>
                </div>

                <p>
                  Although FINOVIX, S.A.P.I. de C.V. is the legal entity responsible for both
                  operations, Popnest (web platform) and Estudio Popnest (physical space) operate
                  with independent business models, terms and conditions, and privacy policies
                  depending on the channel through which the reservation or service contract is
                  made.
                </p>

                <h3>1.2 About Popnest and Associated Spaces</h3>
                <p>
                  Popnest is a digital platform that facilitates connections between users and
                  multiple coworking spaces, offices, and related services. The spaces that appear
                  on our platform (including, among others, Estudio Popnest) operate under their own
                  terms and conditions, privacy policies, and business models, regardless of whether
                  they share the same legal entity with Popnest.
                </p>
                <p>
                  When you make a reservation through Popnest, you establish a commercial
                  relationship with Popnest as a technology platform and with the specific space you
                  select. This Privacy Policy regulates the data processing performed by Popnest as
                  a platform. The selected associated space may have its own additional policies
                  that will apply when you use their physical services.
                </p>
              </div>

              <div class="section">
                <h2>2. PERSONAL DATA WE COLLECT</h2>

                <h3>2.1 Data collected directly from the user</h3>
                <p>
                  When you make a reservation, purchase, or register on our platform, we collect the
                  following personal data:
                </p>

                <h4>Identification data:</h4>
                <ul>
                  <li>Full name</li>
                  <li>Email address</li>
                  <li>Phone number (mobile or landline)</li>
                  <li>Date of birth (when applicable)</li>
                  <li>Nationality</li>
                  <li>Home address (when required for billing)</li>
                </ul>

                <h4>Official identification data (for certain services):</h4>
                <ul>
                  <li>Type of identification (INE/IFE, Passport, Temporary/Permanent Residence)</li>
                  <li>Official identification number</li>
                  <li>Issuing authority</li>
                  <li>Photograph or scan of official identification (for validation)</li>
                </ul>

                <h4>Billing and payment data:</h4>
                <ul>
                  <li>Credit or debit card information (processed by specialized third parties)</li>
                  <li>RFC (Federal Taxpayer Registry) when billing is required</li>
                  <li>Business name (if applicable)</li>
                  <li>Tax address (if applicable)</li>
                  <li>Bank details for transfers (when applicable)</li>
                </ul>

                <h4>Service usage data:</h4>
                <ul>
                  <li>Reservation history</li>
                  <li>Space and service preferences</li>
                  <li>Dates and times of use</li>
                  <li>Payment methods used</li>
                </ul>

                <h4>Communication data:</h4>
                <ul>
                  <li>Messages and inquiries sent through the platform</li>
                  <li>Comments and feedback</li>
                  <li>Record of communications with our customer service team</li>
                </ul>

                <h3>2.2 Automatically collected data</h3>
                <p>
                  When you visit our website, we automatically collect information through
                  technologies such as cookies, web beacons, and analytics tools:
                </p>

                <h4>Navigation data:</h4>
                <ul>
                  <li>IP address</li>
                  <li>Browser type and version</li>
                  <li>Operating system</li>
                  <li>Pages visited and time spent</li>
                  <li>Referrer website</li>
                  <li>Preferred language</li>
                  <li>Screen resolution and device type</li>
                </ul>

                <h4>Interaction data:</h4>
                <ul>
                  <li>Clicks and mouse movements</li>
                  <li>Forms started but not completed</li>
                  <li>Loading or navigation errors</li>
                  <li>Platform usage patterns</li>
                </ul>

                <div class="accent-box">
                  <p>
                    <strong>Cookies and similar technologies:</strong> We use our own and
                    third-party cookies to improve your experience, analyze site usage, and
                    personalize content. You can manage your cookie preferences through your browser
                    settings.
                  </p>
                </div>
              </div>

              <div class="section">
                <h2>3. PURPOSES OF PERSONAL DATA PROCESSING</h2>
                <p>The personal data we collect will be used for the following purposes:</p>

                <h3>3.1 Primary purposes (necessary for service provision)</h3>
                <ul>
                  <li>
                    Process and manage your space reservations through our platform (Day Pass,
                    packages, monthly plans, meeting rooms, etc.)
                  </li>
                  <li>
                    Process payments and manage financial transactions between users and associated
                    spaces
                  </li>
                  <li>Issue tax receipts and invoices when required</li>
                  <li>Manage your user account on the Popnest platform</li>
                  <li>
                    Share necessary information with associated spaces so they can provide the
                    reserved service
                  </li>
                  <li>
                    Validate your identity when required by associated spaces or for platform
                    security reasons
                  </li>
                  <li>
                    Communicate with you about your reservation, confirmations, changes, or
                    cancellations
                  </li>
                  <li>
                    Provide customer service and resolve inquiries or problems related to platform
                    use
                  </li>
                  <li>
                    Comply with contractual obligations arising from commercial relationships with
                    users and associated spaces
                  </li>
                  <li>
                    Maintain platform usage records for security, operation, and service improvement
                  </li>
                </ul>

                <h3>3.2 Secondary purposes (with your consent)</h3>
                <ul>
                  <li>
                    Send promotional communications, special offers, and news about our services
                  </li>
                  <li>Conduct market research, preference analysis, and service improvement</li>
                  <li>Personalize your platform experience according to your preferences</li>
                  <li>Invite you to events, workshops, or special activities</li>
                  <li>Request your opinion through surveys or reviews</li>
                  <li>
                    Share information about complementary services or third-party services related
                    to wellness and productivity
                  </li>
                </ul>

                <p>
                  If you do not wish for your personal data to be processed for these secondary
                  purposes, you may express your objection at any time through the mechanisms
                  indicated in this Policy.
                </p>
              </div>

              <div class="section">
                <h2>4. TRANSFERS OF PERSONAL DATA</h2>

                <h3>4.1 Transfers requiring your consent</h3>
                <p>
                  We do not share your personal data with third parties for commercial purposes
                  without your express consent, except in the cases provided for in this Policy.
                </p>

                <h3>4.2 Transfers without consent (cases provided by law)</h3>
                <p>We may transfer your personal data to third parties in the following cases:</p>

                <h4>Associated spaces:</h4>
                <ul>
                  <li>
                    We share necessary information with the coworking space you select for your
                    reservation, including: name, email address, phone number, reservation date and
                    time, type of service contracted, and any other information required by the
                    space to provide the service
                  </li>
                  <li>
                    Associated spaces are responsible for processing your personal data in
                    accordance with their own privacy policies and terms of use
                  </li>
                  <li>
                    Each associated space may have its own identification requirements, access
                    policies, and contractual terms
                  </li>
                </ul>

                <h4>Payment service providers:</h4>
                <ul>
                  <li>
                    Online payment processors (Stripe, PayPal, or others) for secure transaction
                    processing
                  </li>
                  <li>Banking institutions for transfer processing</li>
                </ul>

                <h4>Technology service providers:</h4>
                <ul>
                  <li>Hosting and cloud storage providers</li>
                  <li>Web analytics and metrics services (Google Analytics or similar)</li>
                  <li>Email marketing service providers (when you have given your consent)</li>
                  <li>Security and fraud prevention service providers</li>
                </ul>

                <h4>Authorities and legal compliance:</h4>
                <ul>
                  <li>When required by law, court order, or competent authority</li>
                  <li>To comply with tax and accounting obligations</li>
                  <li>In case of legal investigations or judicial proceedings</li>
                </ul>

                <h4>Professional services:</h4>
                <ul>
                  <li>
                    Legal advisors, accountants, or auditors when necessary for business operations
                  </li>
                </ul>

                <h3>4.3 Protection measures in transfers</h3>
                <p>When we transfer data to third parties, we ensure that:</p>
                <ul>
                  <li>Third parties comply with security and data protection standards</li>
                  <li>Confidentiality and data protection agreements are established</li>
                  <li>Transfers are made only for authorized purposes</li>
                  <li>Personal data protection principles are respected</li>
                </ul>

                <h3>4.4 Transfers to associated spaces</h3>
                <p>
                  When you make a reservation, we share with the selected associated space the
                  information necessary for them to provide you with the service. This transfer is
                  essential for the fulfillment of the reservation contract.
                </p>
                <p>
                  Associated spaces are independent entities and have their own privacy policies and
                  terms of use. We recommend reviewing each space's privacy policies before making a
                  reservation.
                </p>
                <p>
                  Popnest is not responsible for personal data processing performed by associated
                  spaces once the information has been transferred, except in cases provided by law.
                </p>
              </div>

              <div class="section">
                <h2>5. ARCO RIGHTS (ACCESS, RECTIFICATION, CANCELLATION, AND OPPOSITION)</h2>
                <p>As a data subject, you have the right to:</p>

                <h3>5.1 Access</h3>
                <p>
                  Know what personal data we have about you, what we use it for, and the conditions
                  under which we use it.
                </p>

                <h3>5.2 Rectification</h3>
                <p>
                  Request correction of your personal data when it is inaccurate, incomplete, or not
                  up to date.
                </p>

                <h3>5.3 Cancellation</h3>
                <p>
                  Request that we delete your personal data from our records when you consider that
                  it is not being used in accordance with the principles and duties provided for in
                  the LFPDPPP.
                </p>

                <h3>5.4 Opposition</h3>
                <p>
                  Object to the processing of your personal data for specific purposes, including
                  the secondary purposes mentioned in this Policy.
                </p>

                <h3>5.5 Revocation of consent</h3>
                <p>
                  Revoke at any time the consent you have granted for the processing of your
                  personal data for secondary purposes.
                </p>

                <h3>5.6 Procedure to exercise your ARCO rights</h3>
                <p>To exercise any of these rights, you must submit a request via:</p>

                <div class="contact-info">
                  <h4>
                    Email: <a href="mailto:info@popnest.org">info@popnest.org</a>
                  </h4>
                  <p>
                    <strong>Subject:</strong> "ARCO Rights Request - [Your name]"
                  </p>
                </div>

                <p>Your request must contain:</p>
                <ul>
                  <li>Name of the data subject and email address to receive the response</li>
                  <li>
                    Documents proving the identity of the data subject or, if applicable, legal
                    representation
                  </li>
                  <li>
                    Clear and precise description of the personal data for which you seek to
                    exercise a right
                  </li>
                  <li>
                    Any other element or document that facilitates the location of the personal data
                  </li>
                </ul>

                <p>
                  We will respond to your request within a maximum period of 20 business days from
                  the date we receive it. If your request is valid, we will make it effective within
                  a maximum period of 15 business days.
                </p>
                <p>
                  If the request is not valid according to the LFPDPPP, we will notify you within
                  the aforementioned period, indicating the reason for the denial.
                </p>
              </div>

              <div class="section">
                <h2>6. SECURITY OF PERSONAL DATA</h2>
                <p>
                  We implement technical, administrative, and physical measures to protect your
                  personal data against loss, destruction, alteration, misuse, or unauthorized
                  access, including:
                </p>
                <ul>
                  <li>Encryption of sensitive data in transit and at rest</li>
                  <li>Restricted access controls and user authentication</li>
                  <li>Continuous system monitoring and threat detection</li>
                  <li>Regular backups</li>
                  <li>Staff training on data protection</li>
                  <li>Internal information security policies</li>
                  <li>Constant updating of systems and security software</li>
                </ul>
                <p>
                  However, no method of transmission over the internet or electronic storage is 100%
                  secure. Although we strive to protect your personal data, we cannot guarantee
                  absolute security.
                </p>
              </div>

              <div class="section">
                <h2>7. RETENTION OF PERSONAL DATA</h2>
                <p>
                  We will retain your personal data only for the time necessary to fulfill the
                  purposes for which they were collected and to comply with applicable legal
                  obligations, including:
                </p>
                <ul>
                  <li>Period of active commercial relationship</li>
                  <li>
                    Time necessary to comply with tax and accounting obligations (minimum 5 years
                    according to Mexican tax legislation)
                  </li>
                  <li>Time necessary to resolve disputes or claims</li>
                  <li>Retention periods established by specific legal provisions</li>
                </ul>
                <p>
                  Once these periods are met, we will proceed to cancel your personal data from our
                  systems.
                </p>
              </div>

              <div class="section">
                <h2>8. COOKIES AND TRACKING TECHNOLOGIES</h2>

                <h3>8.1 What are cookies?</h3>
                <p>
                  Cookies are small text files that are stored on your device when you visit our
                  website. They help us improve your experience, remember your preferences, and
                  analyze how you use our platform.
                </p>

                <h3>8.2 Types of cookies we use</h3>
                <h4>Essential cookies:</h4>
                <p>
                  Necessary for the basic functioning of the site and reservation platform. They
                  cannot be disabled.
                </p>

                <h4>Functionality cookies:</h4>
                <p>Allow us to remember your preferences and personalize your experience.</p>

                <h4>Analytics cookies:</h4>
                <p>Help us understand how users interact with our site to improve it.</p>

                <h4>Marketing cookies:</h4>
                <p>
                  Used to show relevant content and measure the effectiveness of our campaigns (only
                  with your consent).
                </p>

                <h3>8.3 Cookie management</h3>
                <p>
                  You can manage or disable cookies through your browser settings. Please note that
                  disabling certain cookies may affect website functionality.
                </p>
              </div>

              <div class="section">
                <h2>9. MINORS</h2>
                <p>
                  Our services are directed exclusively to persons over 18 years of age. We do not
                  intentionally collect personal data from minors. If we discover that we have
                  collected data from a minor without the consent of their parents or guardians, we
                  will take measures to remove that information from our systems.
                </p>
              </div>

              <div class="section">
                <h2>10. LINKS TO THIRD-PARTY WEBSITES</h2>
                <p>
                  Our website may contain links to third-party websites. This Privacy Policy does
                  not apply to third-party websites. We recommend reviewing the privacy policies of
                  any website you visit.
                </p>
              </div>

              <div class="section">
                <h2>11. CHANGES TO THIS PRIVACY POLICY</h2>
                <p>
                  We reserve the right to modify this Privacy Policy at any time to reflect changes
                  in our practices, services, applicable legislation, or for other operational,
                  legal, or regulatory reasons.
                </p>
                <p>
                  Modifications will take effect from their publication on our website. We recommend
                  reviewing this Policy periodically to stay informed about how we protect your
                  personal data.
                </p>
                <p>In case of material changes, we will notify you through:</p>
                <ul>
                  <li>A prominent notice on our website</li>
                  <li>Email to the address registered in your account</li>
                  <li>Notification when logging into the platform</li>
                </ul>
              </div>

              <div class="section">
                <h2>12. CONSENT</h2>
                <p>
                  By accessing and using our website and reservation platform, you accept this
                  Privacy Policy and consent to the processing of your personal data in accordance
                  with the provisions set forth herein.
                </p>
                <p>
                  If you do not agree with this Policy, we ask that you do not use our website or
                  make reservations through our platform.
                </p>
              </div>

              <div class="section">
                <h2>13. CONTACT</h2>
                <p>
                  For any questions, comments, or requests related to this Privacy Policy or the
                  processing of your personal data performed through the Popnest platform, you can
                  contact us:
                </p>

                <div class="contact-info">
                  <h4>Popnest</h4>
                  <p>
                    <strong>FINOVIX, S.A.P.I. de C.V.</strong>
                  </p>
                  <p>
                    Address: Londres 105, Colonia Del Carmen, Coyoacán, 04100 Mexico City, CDMX,
                    Mexico
                  </p>
                  <p>
                    Email: <a href="mailto:info@popnest.org">info@popnest.org</a>
                  </p>
                  <p>
                    Website:{' '}
                    <a href="https://popnest.org" target="_blank">
                      popnest.org
                    </a>
                  </p>
                </div>

                <div class="accent-box">
                  <p>
                    <strong>Important note:</strong> If you made a reservation directly with Estudio
                    Popnest (not through popnest.org), you must contact Estudio Popnest to exercise
                    your ARCO rights or inquiries related to that specific reservation, as their own
                    Privacy Policy will apply.
                  </p>
                </div>
              </div>

              <div class="section">
                <h2>14. CONTROL AUTHORITY</h2>
                <p>
                  If you consider that your personal data protection rights have been violated, you
                  may file a complaint with the National Institute of Transparency, Access to
                  Information and Protection of Personal Data (INAI):
                </p>

                <div class="contact-info">
                  <h4>
                    National Institute of Transparency, Access to Information and Protection of
                    Personal Data (INAI)
                  </h4>
                  <p>
                    Website:{' '}
                    <a href="https://www.inai.org.mx" target="_blank">
                      www.inai.org.mx
                    </a>
                  </p>
                  <p>Phone: 800 835 4324</p>
                </div>
              </div>

              <div class="highlight-box">
                <p>
                  <strong>
                    This Privacy Policy is an integral part of Popnest's Terms and Conditions of
                    Use.
                  </strong>{' '}
                  By using our services through popnest.org, you accept both this Policy and
                  Popnest's Terms and Conditions.
                </p>
              </div>

              <div class="accent-box" style={{marginTop: "40px"}}>
                <p>
                  <strong>IMPORTANT - DIFFERENCES BETWEEN RESERVATION CHANNELS:</strong>
                </p>
                <p>
                  Although FINOVIX, S.A.P.I. de C.V. is the legal entity responsible for both
                  Popnest (web platform) and Estudio Popnest (physical space), different policies
                  and terms apply depending on the channel through which you make your reservation:
                </p>
                <ul style={{marginTop: "15px"}}>
                  <li>
                    If you reserve through popnest.org → This Privacy Policy and Popnest's Terms and
                    Conditions apply.
                  </li>
                  <li>
                    If you reserve directly with Estudio Popnest (at their physical facilities or
                    through their own channels) → Estudio Popnest's Privacy Policy and Terms and
                    Conditions apply.
                  </li>
                </ul>
                <p style={{marginTop: "15px"}}>
                  Each channel operates with its own business model, contractual terms, and privacy
                  policies, even though they share the same legal entity. We recommend reviewing the
                  corresponding policies and terms according to the channel you use to make your
                  reservation.
                </p>
              </div>
            </div>

            <footer>
              <p class="contact-info">Questions or Clarifications: info@popnest.org</p>
            </footer>
          </>
        )}
        {lenguageData === 'ES' && (
          <>
            <div class="header">
              <h1>Política de Privacidad</h1>
              <p>Usuarios de la plataforma de Popnest</p>
              <p style={{ marginTop: '10px', fontSize: '0.95rem' }}>
                Última actualización: 22 de diciembre del 2025
              </p>
            </div>

            <div class="container">
              <div class="section">
                <h2>1. IDENTIFICACIÓN DEL RESPONSABLE</h2>
                <p>
                  FINOVIX, S.A.P.I. de C.V., operando bajo el nombre comercial Popnest (en adelante,
                  "Popnest", "nosotros", "nuestro" o "el Responsable"), con domicilio en Londres
                  105, Colonia Del Carmen, Coyoacán, 04100 Ciudad de México, CDMX, México, es
                  responsable del tratamiento de los datos personales que recabamos de los usuarios
                  que acceden a nuestro sitio web popnest.org y utilizan nuestra plataforma de
                  reservaciones en línea.
                </p>
                <p>
                  Esta Política de Privacidad se rige por la Ley Federal de Protección de Datos
                  Personales en Posesión de los Particulares (LFPDPPP) y demás normativa aplicable
                  en los Estados Unidos Mexicanos.
                </p>

                <h3>1.1 Alcance de esta Política de Privacidad</h3>
                <p>
                  Esta Política de Privacidad aplica exclusivamente a los usuarios que realizan
                  reservaciones, compras o utilizan servicios a través de la plataforma web
                  popnest.org.
                </p>

                <div class="highlight-box">
                  <p>
                    <strong>IMPORTANTE:</strong> FINOVIX, S.A.P.I. de C.V. también opera bajo el
                    nombre comercial "Estudio Popnest" como espacio físico de coworking. Si realizas
                    una reservación directamente con Estudio Popnest (ya sea en sus instalaciones
                    físicas o a través de sus propios canales de reservación), aplicará la Política
                    de Privacidad y los Términos y Condiciones específicos de Estudio Popnest, los
                    cuales pueden diferir de esta Política.
                  </p>
                </div>

                <p>
                  Aunque FINOVIX, S.A.P.I. de C.V. es la entidad legal responsable de ambas
                  operaciones, Popnest (plataforma web) y Estudio Popnest (espacio físico) operan
                  con modelos de negocio, términos y condiciones, y políticas de privacidad
                  independientes según el canal a través del cual se realiza la reservación o
                  contratación del servicio.
                </p>

                <h3>1.2 Sobre Popnest y los Espacios Asociados</h3>
                <p>
                  Popnest es una plataforma digital que facilita la conexión entre usuarios y
                  múltiples espacios de coworking, oficinas y servicios relacionados. Los espacios
                  que aparecen en nuestra plataforma (incluyendo, entre otros, Estudio Popnest)
                  operan bajo sus propios términos y condiciones, políticas de privacidad y modelos
                  de negocio, independientemente de que compartan o no la misma entidad legal con
                  Popnest.
                </p>
                <p>
                  Cuando realizas una reservación a través de Popnest, estableces una relación
                  comercial con Popnest como plataforma tecnológica y con el espacio específico que
                  seleccionas. Esta Política de Privacidad regula el tratamiento de datos realizado
                  por Popnest como plataforma. El espacio asociado seleccionado puede tener sus
                  propias políticas adicionales que aplicarán cuando utilices sus servicios físicos.
                </p>
              </div>

              <div class="section">
                <h2>2. PERSONAL DATA WE COLLECT</h2>

                <h3>2.1 Data collected directly from the user</h3>
                <p>
                  When you make a reservation, purchase, or register on our platform, we collect the
                  following personal data:
                </p>

                <h4>Identification data:</h4>
                <ul>
                  <li>Full name</li>
                  <li>Email address</li>
                  <li>Phone number (mobile or landline)</li>
                  <li>Date of birth (when applicable)</li>
                  <li>Nationality</li>
                  <li>Home address (when required for billing)</li>
                </ul>

                <h4>Official identification data (for certain services):</h4>
                <ul>
                  <li>Type of identification (INE/IFE, Passport, Temporary/Permanent Residence)</li>
                  <li>Official identification number</li>
                  <li>Issuing authority</li>
                  <li>Photograph or scan of official identification (for validation)</li>
                </ul>

                <h4>Billing and payment data:</h4>
                <ul>
                  <li>Credit or debit card information (processed by specialized third parties)</li>
                  <li>RFC (Federal Taxpayer Registry) when billing is required</li>
                  <li>Business name (if applicable)</li>
                  <li>Tax address (if applicable)</li>
                  <li>Bank details for transfers (when applicable)</li>
                </ul>

                <h4>Service usage data:</h4>
                <ul>
                  <li>Reservation history</li>
                  <li>Space and service preferences</li>
                  <li>Dates and times of use</li>
                  <li>Payment methods used</li>
                </ul>

                <h4>Communication data:</h4>
                <ul>
                  <li>Messages and inquiries sent through the platform</li>
                  <li>Comments and feedback</li>
                  <li>Record of communications with our customer service team</li>
                </ul>

                <h3>2.2 Automatically collected data</h3>
                <p>
                  When you visit our website, we automatically collect information through
                  technologies such as cookies, web beacons, and analytics tools:
                </p>

                <h4>Navigation data:</h4>
                <ul>
                  <li>IP address</li>
                  <li>Browser type and version</li>
                  <li>Operating system</li>
                  <li>Pages visited and time spent</li>
                  <li>Referrer website</li>
                  <li>Preferred language</li>
                  <li>Screen resolution and device type</li>
                </ul>

                <h4>Interaction data:</h4>
                <ul>
                  <li>Clicks and mouse movements</li>
                  <li>Forms started but not completed</li>
                  <li>Loading or navigation errors</li>
                  <li>Platform usage patterns</li>
                </ul>

                <div class="accent-box">
                  <p>
                    <strong>Cookies and similar technologies:</strong> We use our own and
                    third-party cookies to improve your experience, analyze site usage, and
                    personalize content. You can manage your cookie preferences through your browser
                    settings.
                  </p>
                </div>
              </div>

              <div class="section">
                <h2>3. PURPOSES OF PERSONAL DATA PROCESSING</h2>
                <p>The personal data we collect will be used for the following purposes:</p>

                <h3>3.1 Primary purposes (necessary for service provision)</h3>
                <ul>
                  <li>
                    Process and manage your space reservations through our platform (Day Pass,
                    packages, monthly plans, meeting rooms, etc.)
                  </li>
                  <li>
                    Process payments and manage financial transactions between users and associated
                    spaces
                  </li>
                  <li>Issue tax receipts and invoices when required</li>
                  <li>Manage your user account on the Popnest platform</li>
                  <li>
                    Share necessary information with associated spaces so they can provide the
                    reserved service
                  </li>
                  <li>
                    Validate your identity when required by associated spaces or for platform
                    security reasons
                  </li>
                  <li>
                    Communicate with you about your reservation, confirmations, changes, or
                    cancellations
                  </li>
                  <li>
                    Provide customer service and resolve inquiries or problems related to platform
                    use
                  </li>
                  <li>
                    Comply with contractual obligations arising from commercial relationships with
                    users and associated spaces
                  </li>
                  <li>
                    Maintain platform usage records for security, operation, and service improvement
                  </li>
                </ul>

                <h3>3.2 Secondary purposes (with your consent)</h3>
                <ul>
                  <li>
                    Send promotional communications, special offers, and news about our services
                  </li>
                  <li>Conduct market research, preference analysis, and service improvement</li>
                  <li>Personalize your platform experience according to your preferences</li>
                  <li>Invite you to events, workshops, or special activities</li>
                  <li>Request your opinion through surveys or reviews</li>
                  <li>
                    Share information about complementary services or third-party services related
                    to wellness and productivity
                  </li>
                </ul>

                <p>
                  If you do not wish for your personal data to be processed for these secondary
                  purposes, you may express your objection at any time through the mechanisms
                  indicated in this Policy.
                </p>
              </div>

              <div class="section">
                <h2>4. TRANSFERS OF PERSONAL DATA</h2>

                <h3>4.1 Transfers requiring your consent</h3>
                <p>
                  We do not share your personal data with third parties for commercial purposes
                  without your express consent, except in the cases provided for in this Policy.
                </p>

                <h3>4.2 Transfers without consent (cases provided by law)</h3>
                <p>We may transfer your personal data to third parties in the following cases:</p>

                <h4>Associated spaces:</h4>
                <ul>
                  <li>
                    We share necessary information with the coworking space you select for your
                    reservation, including: name, email address, phone number, reservation date and
                    time, type of service contracted, and any other information required by the
                    space to provide the service
                  </li>
                  <li>
                    Associated spaces are responsible for processing your personal data in
                    accordance with their own privacy policies and terms of use
                  </li>
                  <li>
                    Each associated space may have its own identification requirements, access
                    policies, and contractual terms
                  </li>
                </ul>

                <h4>Payment service providers:</h4>
                <ul>
                  <li>
                    Online payment processors (Stripe, PayPal, or others) for secure transaction
                    processing
                  </li>
                  <li>Banking institutions for transfer processing</li>
                </ul>

                <h4>Technology service providers:</h4>
                <ul>
                  <li>Hosting and cloud storage providers</li>
                  <li>Web analytics and metrics services (Google Analytics or similar)</li>
                  <li>Email marketing service providers (when you have given your consent)</li>
                  <li>Security and fraud prevention service providers</li>
                </ul>

                <h4>Authorities and legal compliance:</h4>
                <ul>
                  <li>When required by law, court order, or competent authority</li>
                  <li>To comply with tax and accounting obligations</li>
                  <li>In case of legal investigations or judicial proceedings</li>
                </ul>

                <h4>Professional services:</h4>
                <ul>
                  <li>
                    Legal advisors, accountants, or auditors when necessary for business operations
                  </li>
                </ul>

                <h3>4.3 Protection measures in transfers</h3>
                <p>When we transfer data to third parties, we ensure that:</p>
                <ul>
                  <li>Third parties comply with security and data protection standards</li>
                  <li>Confidentiality and data protection agreements are established</li>
                  <li>Transfers are made only for authorized purposes</li>
                  <li>Personal data protection principles are respected</li>
                </ul>

                <h3>4.4 Transfers to associated spaces</h3>
                <p>
                  When you make a reservation, we share with the selected associated space the
                  information necessary for them to provide you with the service. This transfer is
                  essential for the fulfillment of the reservation contract.
                </p>
                <p>
                  Associated spaces are independent entities and have their own privacy policies and
                  terms of use. We recommend reviewing each space's privacy policies before making a
                  reservation.
                </p>
                <p>
                  Popnest is not responsible for personal data processing performed by associated
                  spaces once the information has been transferred, except in cases provided by law.
                </p>
              </div>

              <div class="section">
                <h2>5. ARCO RIGHTS (ACCESS, RECTIFICATION, CANCELLATION, AND OPPOSITION)</h2>
                <p>As a data subject, you have the right to:</p>

                <h3>5.1 Access</h3>
                <p>
                  Know what personal data we have about you, what we use it for, and the conditions
                  under which we use it.
                </p>

                <h3>5.2 Rectification</h3>
                <p>
                  Request correction of your personal data when it is inaccurate, incomplete, or not
                  up to date.
                </p>

                <h3>5.3 Cancellation</h3>
                <p>
                  Request that we delete your personal data from our records when you consider that
                  it is not being used in accordance with the principles and duties provided for in
                  the LFPDPPP.
                </p>

                <h3>5.4 Opposition</h3>
                <p>
                  Object to the processing of your personal data for specific purposes, including
                  the secondary purposes mentioned in this Policy.
                </p>

                <h3>5.5 Revocation of consent</h3>
                <p>
                  Revoke at any time the consent you have granted for the processing of your
                  personal data for secondary purposes.
                </p>

                <h3>5.6 Procedure to exercise your ARCO rights</h3>
                <p>To exercise any of these rights, you must submit a request via:</p>

                <div class="contact-info">
                  <h4>
                    Email: <a href="mailto:info@popnest.org">info@popnest.org</a>
                  </h4>
                  <p>
                    <strong>Subject:</strong> "ARCO Rights Request - [Your name]"
                  </p>
                </div>

                <p>Your request must contain:</p>
                <ul>
                  <li>Name of the data subject and email address to receive the response</li>
                  <li>
                    Documents proving the identity of the data subject or, if applicable, legal
                    representation
                  </li>
                  <li>
                    Clear and precise description of the personal data for which you seek to
                    exercise a right
                  </li>
                  <li>
                    Any other element or document that facilitates the location of the personal data
                  </li>
                </ul>

                <p>
                  We will respond to your request within a maximum period of 20 business days from
                  the date we receive it. If your request is valid, we will make it effective within
                  a maximum period of 15 business days.
                </p>
                <p>
                  If the request is not valid according to the LFPDPPP, we will notify you within
                  the aforementioned period, indicating the reason for the denial.
                </p>
              </div>

              <div class="section">
                <h2>6. SECURITY OF PERSONAL DATA</h2>
                <p>
                  We implement technical, administrative, and physical measures to protect your
                  personal data against loss, destruction, alteration, misuse, or unauthorized
                  access, including:
                </p>
                <ul>
                  <li>Encryption of sensitive data in transit and at rest</li>
                  <li>Restricted access controls and user authentication</li>
                  <li>Continuous system monitoring and threat detection</li>
                  <li>Regular backups</li>
                  <li>Staff training on data protection</li>
                  <li>Internal information security policies</li>
                  <li>Constant updating of systems and security software</li>
                </ul>
                <p>
                  However, no method of transmission over the internet or electronic storage is 100%
                  secure. Although we strive to protect your personal data, we cannot guarantee
                  absolute security.
                </p>
              </div>

              <div class="section">
                <h2>7. RETENTION OF PERSONAL DATA</h2>
                <p>
                  We will retain your personal data only for the time necessary to fulfill the
                  purposes for which they were collected and to comply with applicable legal
                  obligations, including:
                </p>
                <ul>
                  <li>Period of active commercial relationship</li>
                  <li>
                    Time necessary to comply with tax and accounting obligations (minimum 5 years
                    according to Mexican tax legislation)
                  </li>
                  <li>Time necessary to resolve disputes or claims</li>
                  <li>Retention periods established by specific legal provisions</li>
                </ul>
                <p>
                  Once these periods are met, we will proceed to cancel your personal data from our
                  systems.
                </p>
              </div>

              <div class="section">
                <h2>8. COOKIES AND TRACKING TECHNOLOGIES</h2>

                <h3>8.1 What are cookies?</h3>
                <p>
                  Cookies are small text files that are stored on your device when you visit our
                  website. They help us improve your experience, remember your preferences, and
                  analyze how you use our platform.
                </p>

                <h3>8.2 Types of cookies we use</h3>
                <h4>Essential cookies:</h4>
                <p>
                  Necessary for the basic functioning of the site and reservation platform. They
                  cannot be disabled.
                </p>

                <h4>Functionality cookies:</h4>
                <p>Allow us to remember your preferences and personalize your experience.</p>

                <h4>Analytics cookies:</h4>
                <p>Help us understand how users interact with our site to improve it.</p>

                <h4>Marketing cookies:</h4>
                <p>
                  Used to show relevant content and measure the effectiveness of our campaigns (only
                  with your consent).
                </p>

                <h3>8.3 Cookie management</h3>
                <p>
                  You can manage or disable cookies through your browser settings. Please note that
                  disabling certain cookies may affect website functionality.
                </p>
              </div>

              <div class="section">
                <h2>9. MINORS</h2>
                <p>
                  Our services are directed exclusively to persons over 18 years of age. We do not
                  intentionally collect personal data from minors. If we discover that we have
                  collected data from a minor without the consent of their parents or guardians, we
                  will take measures to remove that information from our systems.
                </p>
              </div>

              <div class="section">
                <h2>10. LINKS TO THIRD-PARTY WEBSITES</h2>
                <p>
                  Our website may contain links to third-party websites. This Privacy Policy does
                  not apply to third-party websites. We recommend reviewing the privacy policies of
                  any website you visit.
                </p>
              </div>

              <div class="section">
                <h2>11. CHANGES TO THIS PRIVACY POLICY</h2>
                <p>
                  We reserve the right to modify this Privacy Policy at any time to reflect changes
                  in our practices, services, applicable legislation, or for other operational,
                  legal, or regulatory reasons.
                </p>
                <p>
                  Modifications will take effect from their publication on our website. We recommend
                  reviewing this Policy periodically to stay informed about how we protect your
                  personal data.
                </p>
                <p>In case of material changes, we will notify you through:</p>
                <ul>
                  <li>A prominent notice on our website</li>
                  <li>Email to the address registered in your account</li>
                  <li>Notification when logging into the platform</li>
                </ul>
              </div>

              <div class="section">
                <h2>12. CONSENT</h2>
                <p>
                  By accessing and using our website and reservation platform, you accept this
                  Privacy Policy and consent to the processing of your personal data in accordance
                  with the provisions set forth herein.
                </p>
                <p>
                  If you do not agree with this Policy, we ask that you do not use our website or
                  make reservations through our platform.
                </p>
              </div>

              <div class="section">
                <h2>13. CONTACT</h2>
                <p>
                  For any questions, comments, or requests related to this Privacy Policy or the
                  processing of your personal data performed through the Popnest platform, you can
                  contact us:
                </p>

                <div class="contact-info">
                  <h4>Popnest</h4>
                  <p>
                    <strong>FINOVIX, S.A.P.I. de C.V.</strong>
                  </p>
                  <p>
                    Address: Londres 105, Colonia Del Carmen, Coyoacán, 04100 Mexico City, CDMX,
                    Mexico
                  </p>
                  <p>
                    Email: <a href="mailto:info@popnest.org">info@popnest.org</a>
                  </p>
                  <p>
                    Website:{' '}
                    <a href="https://popnest.org" target="_blank">
                      popnest.org
                    </a>
                  </p>
                </div>

                <div class="accent-box">
                  <p>
                    <strong>Important note:</strong> If you made a reservation directly with Estudio
                    Popnest (not through popnest.org), you must contact Estudio Popnest to exercise
                    your ARCO rights or inquiries related to that specific reservation, as their own
                    Privacy Policy will apply.
                  </p>
                </div>
              </div>

              <div class="section">
                <h2>14. CONTROL AUTHORITY</h2>
                <p>
                  If you consider that your personal data protection rights have been violated, you
                  may file a complaint with the National Institute of Transparency, Access to
                  Information and Protection of Personal Data (INAI):
                </p>

                <div class="contact-info">
                  <h4>
                    National Institute of Transparency, Access to Information and Protection of
                    Personal Data (INAI)
                  </h4>
                  <p>
                    Website:{' '}
                    <a href="https://www.inai.org.mx" target="_blank">
                      www.inai.org.mx
                    </a>
                  </p>
                  <p>Phone: 800 835 4324</p>
                </div>
              </div>

              <div class="highlight-box">
                <p>
                  <strong>
                    This Privacy Policy is an integral part of Popnest's Terms and Conditions of
                    Use.
                  </strong>{' '}
                  By using our services through popnest.org, you accept both this Policy and
                  Popnest's Terms and Conditions.
                </p>
              </div>

              <div class="accent-box" style={{marginTop: "40px"}}>
                <p>
                  <strong>IMPORTANT - DIFFERENCES BETWEEN RESERVATION CHANNELS:</strong>
                </p>
                <p>
                  Although FINOVIX, S.A.P.I. de C.V. is the legal entity responsible for both
                  Popnest (web platform) and Estudio Popnest (physical space), different policies
                  and terms apply depending on the channel through which you make your reservation:
                </p>
                <ul style={{marginTop: "15px"}}>
                  <li>
                    If you reserve through popnest.org → This Privacy Policy and Popnest's Terms and
                    Conditions apply.
                  </li>
                  <li>
                    If you reserve directly with Estudio Popnest (at their physical facilities or
                    through their own channels) → Estudio Popnest's Privacy Policy and Terms and
                    Conditions apply.
                  </li>
                </ul>
                <p style={{marginTop: "15px"}}>
                  Each channel operates with its own business model, contractual terms, and privacy
                  policies, even though they share the same legal entity. We recommend reviewing the
                  corresponding policies and terms according to the channel you use to make your
                  reservation.
                </p>
              </div>
            </div>

            <footer>
              <p class="contact-info">Dudas o aclaraciones: info@popnest.org</p>
            </footer>
          </>
        )}
      </Label>
    </FlexContainer>
  );
};

export default PrivacyPolicy;
